/**
 * Obtiene la fecha actual en formato YYYY-MM-DD
 * @returns {string} Fecha actual
 */

export const getToday = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Obtiene el rango de fechas para la semana actual (lunes a domingo)
 * @returns {object} { startDate, endDate }
 */

export const getWeekRange = () => {
  const today = new Date();
  const currentDay = today.getDay();
  
  // calcular días desde el lunes (0=domingo, 1=lunes, etc.)
  const daysFromMonday = currentDay === 0 ? 6 : currentDay - 1;
  
  // fecha de inicio (lunes)
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - daysFromMonday);
  
  // fecha de fin (domingo)
  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 6);
  
  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0]
  };
};

/**
 * Obtiene el rango de fechas para el mes actual
 * @returns {object} { startDate, endDate }
 */

export const getMonthRange = () => {
  const today = new Date();
  
  // primer día del mes
  const startDate = new Date(today.getFullYear(), today.getMonth(), 1);
  
  // último día del mes
  const endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  
  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0]
  };
};

/**
 * Verifica si una fecha está dentro de un rango
 * @param {string} fecha - Fecha a verificar (formato backend: 2025-08-12 11:20)
 * @param {string} startDate - Fecha de inicio del rango
 * @param {string} endDate - Fecha de fin del rango
 * @returns {boolean}
 */

export const isInDateRange = (fecha, startDate, endDate) => {
  if (!fecha || !startDate || !endDate) return false;
  
  // extraer solo la fecha del formato backend (2025-08-12 11:20 -> 2025-08-12)
  const fechaSoloFecha = fecha.includes(' ') ? fecha.split(' ')[0] : fecha;
  
  const targetDate = new Date(fechaSoloFecha + 'T00:00:00');
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T23:59:59');
  
  return targetDate >= start && targetDate <= end;
};

/**
 * Verifica si una fecha es hoy
 * @param {string} fecha - Fecha a verificar (formato backend: 2025-08-12 11:20)
 * @returns {boolean}
 */

export const isToday = (fecha) => {
  if (!fecha) {
    return false;
  }
  
  // extraer solo la fecha del formato backend
  const fechaSoloFecha = fecha.includes(' ') ? fecha.split(' ')[0] : fecha;
  const hoy = getToday();
  
  return fechaSoloFecha === hoy;
};

/**
 * Verifica si una fecha está en esta semana
 * @param {string} fecha - Fecha a verificar (formato backend: 2025-08-12 11:20)
 * @returns {boolean}
 */

export const isThisWeek = (fecha) => {
  if (!fecha) return false;
  const { startDate, endDate } = getWeekRange();
  return isInDateRange(fecha, startDate, endDate);
};

/**
 * Verifica si una fecha está en este mes
 * @param {string} fecha - Fecha a verificar (formato backend: 2025-08-12 11:20)
 * @returns {boolean}
 */

export const isThisMonth = (fecha) => {
  if (!fecha) return false;
  const { startDate, endDate } = getMonthRange();
  return isInDateRange(fecha, startDate, endDate);
};

/**
 * Filtrar array de reembolsos por rango de fecha
 * @param {Array} reembolsos - Array de reembolsos
 * @param {string} filterType - 'today'|'week'|'month'|'all'
 * @returns {Array} Array filtrado
 */

export const filterReembolsosByDate = (reembolsos, filterType) => {
  if (!reembolsos || !Array.isArray(reembolsos)) return [];
  
  switch (filterType) {
    case 'today':
      return reembolsos.filter(r => isToday(r.fecha));
    
    case 'week':
      return reembolsos.filter(r => isThisWeek(r.fecha));
    
    case 'month':
      return reembolsos.filter(r => isThisMonth(r.fecha));
    
    case 'all':
    default:
      return reembolsos;
  }
};

/**
 * Obtiene texto descriptivo para el filtro activo
 * @param {string} filterType - Tipo de filtro
 * @returns {string} Descripción del filtro
 */

export const getFilterDescription = (filterType) => {
  const today = getToday();
  const { startDate: weekStart, endDate: weekEnd } = getWeekRange();
  const { startDate: monthStart, endDate: monthEnd } = getMonthRange();
  
  switch (filterType) {
    case 'today':
      return `Hoy (${today})`;
    
    case 'week':
      return `Esta semana (${weekStart} - ${weekEnd})`;
    
    case 'month':
      return `Este mes (${monthStart} - ${monthEnd})`;
    
    case 'all':
    default:
      return 'Todos los períodos';
  }
};

/**
 * Formatea fecha para mostrar en interfaz
 * @param {string} fecha - Fecha en formato backend (2025-08-12 11:20)
 * @returns {string} Fecha formateada
 */

export const formatDateForDisplay = (fecha) => {
  if (!fecha) return '';
  
  // extraer solo la fecha del formato backend
  const fechaSoloFecha = fecha.includes(' ') ? fecha.split(' ')[0] : fecha;
  
  const date = new Date(fechaSoloFecha + 'T00:00:00');
  return date.toLocaleDateString('es-MX', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

const formatFechaHora = (iso) => {
  const d = new Date(iso)
  return `${d.toLocaleDateString('es-MX')} ${d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: true })}`
}

export const formatTareaDescription = (tarea, usuarios) => {
  const creadoPor = usuarios.find(u => u.usuarioID === tarea.usuarioIDCreaTarea)?.nombre || 'Desconocido'
  const asignado = usuarios.find(u => u.usuarioID === tarea.usuarioIDAsignado)?.nombre || 'Sin asignar'
  const fecha = formatFechaHora(tarea.fechaInicio)
  return { linea1: `Creado por: ${creadoPor}  Fecha: ${fecha}`, linea2: `Asignado a: ${asignado}  Fecha: ${fecha}` }
}

export default {
  getToday,
  getWeekRange,
  getMonthRange,
  isInDateRange,
  isToday,
  isThisWeek,
  isThisMonth,
  filterReembolsosByDate,
  getFilterDescription,
  formatDateForDisplay
};